import Layout from 'components/layout';

import React, { FC, PropsWithChildren } from 'react';

import { graphql, HeadFC } from 'gatsby';
import {
  Accordion,
  BackNForth,
  Box,
  ComponentLink,
  ComponentImage,
  Container,
  Heading,
  Hero,
  Link,
  Text,
  ComparisonCardGroup,
  PreFooterCta,
  LucidHead,
} from '@lucid/components-react';
import type {
  Drupal,
  Drupal_NodeSelfServe,
  Drupal_ParagraphComparisonCard,
  Drupal_ParagraphComparisonCards,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphFaqItem,
  Drupal_ParagraphFaqSection,
  Drupal_ParagraphFlexiCustomerLogos,
  Drupal_ParagraphNewBackNForthSections,
  Drupal_ParagraphNewHeroSection,
  Drupal_ParagraphPreFooterSection,
} from 'generated/drupal-graphql';
import { getEntityFields, getI18nData } from '@lucid/utils';

import type { Lucid, Template } from 'types/types';
import FlexiCustomerLogos from 'components/flexi/flexi-customer-logos/flexi-customer-logos';
import { mapBackNForthSections, mapComparisonCards } from 'utilities/mapComponents';

const SelfServe: FC<PropsWithChildren<Template.PageProps>> = ({ data, pageContext }) => {
  const { shortLangPrefix, pageType, nid, shouldHideSsr, i18nKey } = pageContext;
  const i18nData = data.i18nData;
  const drupal = data.drupal as Drupal;
  const node = drupal.nodeById as Drupal_NodeSelfServe;
  const i18n = getI18nData(i18nData, i18nKey);
  const { entityTranslations } = getEntityFields(node);

  // Hero
  const heroSection = node.fieldHeroSection?.entity as Drupal_ParagraphNewHeroSection;
  const heroCtaLinkButton = heroSection.fieldCtaButtons?.[0].entity as Drupal_ParagraphCtaButton;
  const heroButton: ComponentLink = {
    linkUrl: heroCtaLinkButton.fieldButtonLink!,
    linkText: heroCtaLinkButton?.fieldButtonText!,
    isSecondary: heroCtaLinkButton?.fieldIsSecondary!,
  };

  // Logos
  const { fieldCustomerLogos } = node;

  // Comparison Cards
  const comparisonCardGroup = node.fieldComparisonCards?.entity as Drupal_ParagraphComparisonCards;
  const comparisonCardList =
    comparisonCardGroup?.fieldCardsList?.map(card => {
      return card.entity as Drupal_ParagraphComparisonCard;
    }) || [];
  const comparisonCards = mapComparisonCards(comparisonCardList);

  // BackNForth
  const newBackNForthSections = node.fieldNewBackNForthSections?.entity as Drupal_ParagraphNewBackNForthSections;
  const backNForthSections = mapBackNForthSections(newBackNForthSections);

  // FAQ Section
  const faqSection = node.fieldFaqSection?.entity as Drupal_ParagraphFaqSection;

  // Pre-Footer Section
  const preFooter = node.fieldPreFooterSection?.entity as Drupal_ParagraphPreFooterSection;

  return (
    <Layout
      entityTranslations={entityTranslations}
      nid={nid}
      pageType={pageType}
      shortLangPrefix={shortLangPrefix}
      shouldHideSsr={shouldHideSsr}
    >
      {heroSection && (
        <Container>
          <Hero
            shouldImageStack
            css={{
              '> div': {
                maxWidth: '550px',
                justifyContent: 'center',
              },
            }}
            cta={heroButton}
            header={heroSection.fieldHeader as string}
            image={
              {
                imageSrc: heroSection.fieldImageLink,
                imageAlt: '',
              } as ComponentImage
            }
            subheader={(heroSection.fieldSubheader as string) || (heroSection.fieldLongSubheader as string)}
          />
        </Container>
      )}
      {fieldCustomerLogos && (
        <Container
          css={(theme: Lucid.Theme) => ({
            marginBottom: theme.space.s80,
          })}
          extraPadding={3}
        >
          <FlexiCustomerLogos
            hasHeadingBelow
            entity={fieldCustomerLogos?.entity as Drupal_ParagraphFlexiCustomerLogos}
          />
        </Container>
      )}
      {comparisonCardGroup && (
        <Container
          css={(theme: Lucid.Theme) => ({
            background: theme.colors.coolGrey[5],
            margin: 0,
            padding: `${theme.space.s120}px 0`,
            maxWidth: '100%',
          })}
        >
          <ComparisonCardGroup
            comparisonCards={comparisonCards}
            heading={comparisonCardGroup.fieldTitle}
            subheading={comparisonCardGroup.fieldSubtitle}
          />
        </Container>
      )}
      {newBackNForthSections && (
        <Container
          css={{
            overflow: 'hidden',
          }}
        >
          <Box
            css={(theme: Lucid.Theme) => ({
              display: 'flex',
              flexDirection: 'column',
              margin: 'auto',
              textAlign: 'center',
              maxWidth: '780px',
              marginBottom: `-${theme.space.s48}px`,
            })}
          >
            <Heading
              as="h2"
              css={(theme: Lucid.Theme) => [
                {
                  marginTop: theme.space.s120,
                  marginBottom: 0,
                  overflowWrap: 'anywhere',
                },
              ]}
              variant="headline2XL"
            >
              {newBackNForthSections?.fieldTitle}
            </Heading>
            {newBackNForthSections?.fieldSubtitle && (
              <Text variant="paragraphS400">{newBackNForthSections?.fieldSubtitle}</Text>
            )}
          </Box>
          <BackNForth headingVariant="headlineXL" sections={backNForthSections} />
        </Container>
      )}
      {faqSection && (
        <Box
          as="section"
          css={(theme: Lucid.Theme) => ({
            backgroundColor: theme.colors.coolGrey[100],
            paddingTop: theme.space.s80,
          })}
        >
          <Container>
            <Heading
              as="h2"
              css={(theme: Lucid.Theme) => ({
                marginBottom: theme.space[6],
                color: theme.colors.white,
              })}
              variant="headline2XL"
            >
              {faqSection.fieldFaqSectionHeader}
            </Heading>
            <Accordion allowMultipleExpanded allowZeroExpanded>
              {faqSection.fieldFaqItem?.map(faqItem => {
                const entity = faqItem?.entity as Drupal_ParagraphFaqItem;
                const { fieldHeader, fieldDescription, fieldFaqItemLink } = entity;
                return (
                  <Accordion.Item key={fieldHeader} variant="transparent">
                    <Accordion.Heading variant="transparent">
                      <Accordion.Button hasArrow variant="transparent">
                        {fieldHeader}
                      </Accordion.Button>
                    </Accordion.Heading>
                    <Accordion.Panel variant="transparent">
                      <Text
                        css={(theme: Lucid.Theme) => ({
                          color: theme.colors.white,
                          flex: '0 1 100%',
                          maxWidth: '100%',
                        })}
                        variant="paragraphS400"
                      >
                        {fieldDescription}
                      </Text>
                      {!!fieldFaqItemLink?.length && (
                        <ul
                          css={(theme: Lucid.Theme) => ({
                            listStyleType: 'none',
                            margin: theme.space.s0,
                            lineHeight: theme.lineHeights.subNavLink,
                          })}
                        >
                          {fieldFaqItemLink.map(link => (
                            <li key={link?.url?.path}>
                              <Link
                                newTab
                                iconAnimation="navigational"
                                to={link?.url?.path}
                                underline="hover"
                                variant="primaryInverse"
                              >
                                {link?.title || i18n('mktg_ct_consideration_faq_link_text')}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Container>
        </Box>
      )}
      {preFooter?.fieldHeading && (
        <PreFooterCta
          backgroundColor="blue.10"
          ctas={[preFooter.fieldCtaButtonNew?.entity] as Drupal_ParagraphCtaButton[]}
          header={preFooter.fieldHeading!}
        />
      )}
    </Layout>
  );
};

export default SelfServe;

export const Head: HeadFC<Template.PageProps['data'], Template.PageProps['pageContext']> = ({ ...props }) => (
  <LucidHead<Drupal_NodeSelfServe> {...props} />
);

export const query = graphql`
  query ($nid: String!, $i18nKey: String!, $LanguageId: Drupal_LanguageId!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeSelfServe {
          ...entityFields
          fieldHeroSection {
            entity {
              ... on Drupal_ParagraphNewHeroSection {
                fieldHeader
                fieldImageLink
                fieldLongSubheader
                fieldSubheader
                fieldTitlePiece
                fieldCtaButtons {
                  entity {
                    ... on Drupal_ParagraphCtaButton {
                      fieldButtonLink
                      fieldButtonText
                      fieldIsSecondary
                    }
                  }
                }
              }
            }
          }
          fieldCustomerLogos {
            entity {
              ... on Drupal_ParagraphFlexiCustomerLogos {
                fieldCustomerLogosHeader {
                  processed
                }
                fieldCustomerLogosLink {
                  title
                  url {
                    path
                  }
                }
                fieldLogosSection {
                  entity {
                    ... on Drupal_ParagraphExternalLogoSection {
                      id
                      fieldExternalLogos {
                        entity {
                          ... on Drupal_ParagraphExternalLogo {
                            fieldLogoSlug
                          }
                        }
                      }
                      fieldLogoLayoutVariant
                      fieldLogosColor
                      fieldRowHeight
                    }
                  }
                }
              }
            }
          }
          fieldComparisonCards {
            entity {
              ... on Drupal_ParagraphComparisonCards {
                fieldSubtitle
                fieldTitle
                fieldCardsList {
                  entity {
                    ... on Drupal_ParagraphComparisonCard {
                      fieldComparisonCardTitle
                      fieldImage {
                        entity {
                          ... on Drupal_ParagraphSeoImage {
                            fieldImageLink
                            fieldAltText
                          }
                        }
                      }
                      fieldCta {
                        url {
                          path
                        }
                        title
                      }
                      fieldComparisonSections {
                        entity {
                          ... on Drupal_ParagraphComparisonSection {
                            fieldComparisonSectionHeader
                            fieldComparisonSectionList
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          fieldNewBackNForthSections {
            entity {
              ... on Drupal_ParagraphNewBackNForthSections {
                fieldTitle
                fieldSubtitle
                fieldSectionCta {
                  url {
                    path
                  }
                  title
                }
                fieldBackNForthSections {
                  entity {
                    ... on Drupal_ParagraphNewBackNForthSection {
                      fieldTitle
                      fieldSectionDescription {
                        processed
                      }
                      fieldSectionImage {
                        entity {
                          ... on Drupal_ParagraphSeoImage {
                            fieldAltText
                            fieldImageLink
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          fieldFaqSection {
            entity {
              ... on Drupal_ParagraphFaqSection {
                fieldFaqSectionHeader
                fieldFaqItem {
                  entity {
                    ... on Drupal_ParagraphFaqItem {
                      fieldDescription
                      fieldHeader
                      fieldFaqItemLink {
                        url {
                          path
                        }
                        title
                      }
                    }
                  }
                }
              }
            }
          }
          fieldPreFooterSection {
            entity {
              ... on Drupal_ParagraphPreFooterSection {
                fieldHeading
                fieldCtaButtonNew {
                  entity {
                    ... on Drupal_ParagraphCtaButton {
                      fieldIsSecondary
                      fieldButtonText
                      fieldButtonLink
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" }, name: { in: [$i18nKey, "en"] } }) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_consideration_faq_link_text
          }
        }
      }
    }
  }
`;
